import { useEffect, useMemo, useRef } from 'react';
import { Variables } from '@gosupersimple/penguino';
import { Completion, startCompletion } from '@codemirror/autocomplete';

import { CodeMirror, CodeMirrorRef } from '@/components/codemirror';
import { Tooltip } from '@/components/tooltip';
import { ShortcutIndicator } from '@/components/shortcut-indicator';
import { Field } from '@/explore/types';
import { FieldGroup } from '@/explore/pipeline/utils';
import {
  convertFieldsForPenguinoContext,
  dehumanizeExpression,
  humanizeExpression,
} from '@/explore/utils/penguino';

import { penguino } from './penguino';
import { penguinoStateProvider, updatePenguinoState } from './penguino/state';

import styles from './penguino-input.module.scss';

export const PenguinoVersion = '1';

interface PenguinoInputProps {
  value: string;
  placeholder?: string;
  fields: (Field | FieldGroup)[];
  variables: Variables;
  requiredType?: string;
  onChange: (value: string) => void;
  autocompleteSort?: (a: Completion, b: Completion) => number;
}

export const PenguinoInput = (props: PenguinoInputProps) => {
  const { variables, requiredType, autocompleteSort } = props;

  const codeEditorRef = useRef<CodeMirrorRef>(null);

  const namespacedFields = useMemo(
    () => convertFieldsForPenguinoContext(props.fields),
    [props.fields],
  );

  const humanizedValue = useMemo(
    () => humanizeExpression(props.value, namespacedFields),
    [props.value, namespacedFields],
  );

  const autocompleteOptions =
    autocompleteSort !== undefined ? { compareCompletions: autocompleteSort } : undefined;

  const handleHintClick = () => {
    const view = codeEditorRef.current?.view;
    if (view !== undefined) {
      view.focus();
      startCompletion(view);
    }
  };

  useEffect(() => {
    const view = codeEditorRef.current?.view;
    if (view === undefined) {
      return;
    }

    updatePenguinoState(view, { fields: namespacedFields, variables, requiredType });
  }, [namespacedFields, variables, requiredType]);

  return (
    <>
      <CodeMirror
        ref={codeEditorRef}
        value={humanizedValue}
        placeholder={props.placeholder}
        onChange={(value) => props.onChange(dehumanizeExpression(value, namespacedFields))}
        className={styles.penguinoInput}
        autoFocus
        extensions={[penguinoStateProvider(), penguino(autocompleteOptions)]}
        onInitialize={(view) =>
          updatePenguinoState(view, { fields: namespacedFields, variables, requiredType })
        }
      />
      <div className={styles.hint}>
        <Tooltip
          content={
            <span>
              <ShortcutIndicator shortcut={['ctrl', 'space']} size="small" /> to open
            </span>
          }>
          <button onMouseDown={(e) => e.preventDefault()} onClick={handleHintClick} type="button">
            Show suggestions
          </button>
        </Tooltip>
        <a
          href="https://docs.supersimple.io/guides/custom-formulas"
          target="_blank"
          rel="noopener noreferrer">
          View docs
        </a>
      </div>
    </>
  );
};
